/**
 * @fileoverview Own the administration editor for document quotas, retention, and sharing policy. Shared metadata queries supply saved configuration; explicit patch requests submit drafts. `kc-internal-backend/functions/document_governance.py` owns enforcement. Displayed policy and accepted policy remain distinct, and changing this form does not itself perform deletion or a transfer.
 */

import useDocumentData from '../../store/Documents/useDocumentData'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import api from '../../api'
import './DocumentGovernanceSettings.css'


const MB = 1024 * 1024



function bytesLabel(value) {
  const bytes = Number(value || 0)
  if (!bytes) return '0 B'
  if (bytes >= 1024 * MB) return `${(bytes / (1024 * MB)).toFixed(1)} GB`
  return `${Math.round(bytes / MB)} MB`
}



function quotaLabel(value) {
  return Number(value || 0) ? bytesLabel(value) : 'Unlimited'
}



function dateLabel(value) {
  if (!value) return 'No successful sync yet'
  return new Date(Number(value) * 1000).toLocaleString()
}



export function governanceAuditUrl() {
  const base = String(api.defaults?.baseURL || '/api').replace(/\/$/, '')
  const path = '/api/admin/documents/governance/audit.csv'
  return `${base.replace(/\/api$/, '')}${path}`
}



export function DocumentGovernanceStatus({ summary }) {
  if (!summary) return null
  const { storage = {}, availability = {}, processing = {}, offline = {} } = summary
  return (
    <section className="document-governance__status" aria-label="Document availability">
      <article>
        <strong>{bytesLabel(storage.used_bytes)}</strong>
        <span>{storage.quota_bytes ? `of ${quotaLabel(storage.quota_bytes)} used` : 'personal storage used'}</span>
      </article>
      <article>
        <strong>{availability.local_documents || 0} local</strong>
        <span>{availability.remote_only_documents || 0} remote only</span>
      </article>
      <article>
        <strong>{processing.pending || 0} processing</strong>
        <span>{processing.failed || 0} need attention</span>
      </article>
      <article>
        <strong>Last sync</strong>
        <span>{dateLabel(availability.last_successful_sync_at)}</span>
      </article>
      <article>
        <strong>{offline.supported ? 'Offline ready' : 'Download for offline use'}</strong>
        <span>{offline.message}</span>
      </article>
    </section>
  )
}



function numberValue(bytes) {
  return Math.round(Number(bytes || 0) / MB)
}



function formFromSettings(settings) {
  return {
    userQuotaMb: numberValue(settings?.quotas?.user_default_bytes),
    matterQuotaMb: numberValue(settings?.quotas?.matter_default_bytes),
    teamQuotaMb: numberValue(settings?.quotas?.team_default_bytes),
    maxFileMb: numberValue(settings?.transfers?.max_file_bytes),
    maxBatchMb: numberValue(settings?.transfers?.max_batch_bytes),
    extensions: (settings?.transfers?.allowed_extensions || []).join(', '),
    retentionDays: Number(settings?.lifecycle?.retention_days || 30),
    permanentDeletion: Boolean(settings?.lifecycle?.permanent_deletion_enabled),
    externalSharing: Boolean(settings?.sharing?.external_enabled),
    malwareRequired: Boolean(settings?.malware?.scan_required),
    blockScanErrors: Boolean(settings?.malware?.block_on_scan_error),
    malwareProvider: settings?.malware?.provider || '',
  }
}



function errorText(error, fallback) {
  return error?.response?.data?.error || fallback
}



export default function DocumentGovernanceSettings() {
  const dataCache = useDocumentData()
  const [overview, setOverview] = useState(null)
  const [summary, setSummary] = useState(null)
  const [failures, setFailures] = useState(null)
  const [loadErrors, setLoadErrors] = useState({})
  const [savedForm, setSavedForm] = useState(null)
  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const mountedRef = useRef(true)

  const dirtyRef = useRef(false)
  dirtyRef.current = Boolean(form && savedForm && JSON.stringify(form) !== JSON.stringify(savedForm))
  const load = useCallback(

  ({ force = false } = {}) => {
    const policyUrl = '/admin/documents/governance/policy'
    setLoading(!dataCache.read(policyUrl))
    setError('')
    setLoadErrors({})
    const onError =

    (section) => (requestError) => {
      if (section === 'policy') setLoading(false)
      setLoadErrors(current => ({ ...current, [section]: errorText(requestError, `Could not load ${section}.`) }))
    }
    return Promise.all([
      dataCache.load('governance-policy', policyUrl, (data) => {
        setLoading(false)
        if (!dirtyRef.current) {
          const nextForm = formFromSettings(data.settings)
          setForm(nextForm)
          setSavedForm(nextForm)
        }
      }, { force, onError: onError('policy'), onClear: () => { setForm(null); setSavedForm(null) } }),
      dataCache.load('governance', '/admin/documents/governance', setOverview,
        { force, onError: onError('storage statistics'), onClear: () => setOverview(null) }),
      dataCache.load('governance-summary', '/simul/archive/governance/summary', (data) => setSummary(data.summary || null),
        { force, onError: onError('document availability'), onClear: () => setSummary(null) }),
      dataCache.load('governance-failures', '/admin/documents/governance/failures', (data) => setFailures(data.failures || []),
        { force, onError: onError('failed operations'), onClear: () => setFailures(null) }),
    ]).catch(() => null)
  }, [dataCache])

  useEffect(() => {
    mountedRef.current = true
    load()
    return () => { mountedRef.current = false }
  }, [load])

  const dirty = useMemo(() => form && savedForm && JSON.stringify(form) !== JSON.stringify(savedForm), [form, savedForm])
  const change =

  (field, value) => {
    setNotice('')
    setForm(current => ({ ...current, [field]: value }))
  }
  const save =

  async () => {
    if (saving || !dirty) return
    setSaving(true)
    setError('')
    setNotice('')
    try {
      const response = await api.patch('/admin/documents/governance', {
        quotas: {
          user_default_bytes: Number(form.userQuotaMb || 0) * MB,
          matter_default_bytes: Number(form.matterQuotaMb || 0) * MB,
          team_default_bytes: Number(form.teamQuotaMb || 0) * MB,
        },
        transfers: {
          max_file_bytes: Number(form.maxFileMb || 0) * MB,
          max_batch_bytes: Number(form.maxBatchMb || 0) * MB,
          allowed_extensions: form.extensions.split(',').map(value => value.trim()).filter(Boolean),
        },
        lifecycle: {
          retention_days: Number(form.retentionDays),
          permanent_deletion_enabled: form.permanentDeletion,
        },
        sharing: { external_enabled: form.externalSharing },
        malware: {
          scan_required: form.malwareRequired,
          block_on_scan_error: form.blockScanErrors,
          provider: form.malwareProvider,
        },
      })
      const nextForm = formFromSettings(response.data?.settings)
      setForm(nextForm)
      setSavedForm(nextForm)
      setNotice('Document governance policy saved.')
    } catch (requestError) {
      setError(errorText(requestError, 'Document governance policy could not be saved.'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="document-governance" role="status" aria-live="polite">Loading document governance…</div>
  if (!form) return <div className="document-governance document-governance--error" role="alert">{loadErrors.policy || error}</div>

  return (
    <div className="document-governance" aria-busy={saving || undefined}>
      <header className="document-governance__header">
        <div>
          <span>Documents</span>
          <h2>Storage and governance</h2>
          <p>Set firm safeguards and review document availability.</p>
        </div>
        <div className="document-governance__actions">
          <a href={governanceAuditUrl()} download>Export audit CSV</a>
          <button type="button" onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
          </button>
        </div>
      </header>

      {error ? <p className="document-governance__message is-error" role="alert">{error}</p> : null}
      {Object.entries(loadErrors).map(([section, message]) => <p key={section} className="document-governance__message is-error" role="alert">{message}</p>)}
      {notice ? <p className="document-governance__message is-success" role="status">{notice}</p> : null}
      {summary ? <DocumentGovernanceStatus summary={summary} /> : !loadErrors['document availability'] && <p role="status">Loading document availability…</p>}

      <div className="document-governance__grid">
        <section>
          <h3>Storage limits</h3>
          <p>Use 0 for no limit. Existing files remain available when a limit is lowered.</p>
          <label>User default <input aria-label="User quota in MB" type="number" min="0" value={form.userQuotaMb} onChange={event => change('userQuotaMb', event.target.value)} /> MB</label>
          <label>Matter default <input aria-label="Matter quota in MB" type="number" min="0" value={form.matterQuotaMb} onChange={event => change('matterQuotaMb', event.target.value)} /> MB</label>
          <label>Team default <input aria-label="Team quota in MB" type="number" min="0" value={form.teamQuotaMb} onChange={event => change('teamQuotaMb', event.target.value)} /> MB</label>
          <footer>{overview ? `${bytesLabel(overview.storage?.total_bytes)} stored across ${overview.storage?.document_count || 0} documents` : loadErrors['storage statistics'] ? 'Storage statistics unavailable' : 'Loading storage statistics…'}</footer>
        </section>

        <section>
          <h3>Uploads</h3>
          <label>Maximum file <input aria-label="Maximum file size in MB" type="number" min="1" value={form.maxFileMb} onChange={event => change('maxFileMb', event.target.value)} /> MB</label>
          <label>Maximum batch <input aria-label="Maximum batch size in MB" type="number" min="1" value={form.maxBatchMb} onChange={event => change('maxBatchMb', event.target.value)} /> MB</label>
          <label className="is-stacked">Allowed extensions <textarea aria-label="Allowed file extensions" value={form.extensions} onChange={event => change('extensions', event.target.value)} /></label>
        </section>

        <section>
          <h3>Lifecycle</h3>
          <label>Trash retention <input aria-label="Trash retention days" type="number" min="1" max="3650" value={form.retentionDays} onChange={event => change('retentionDays', event.target.value)} /> days</label>
          <label className="is-check"><input type="checkbox" checked={form.permanentDeletion} onChange={event => change('permanentDeletion', event.target.checked)} /> Allow permanent deletion after confirmation and retention checks</label>
          <label className="is-check"><input type="checkbox" checked={form.externalSharing} onChange={event => change('externalSharing', event.target.checked)} /> Allow external share links</label>
          <footer>{overview ? `${overview.legal_hold_count || 0} items under legal hold` : loadErrors['storage statistics'] ? 'Legal hold count unavailable' : 'Loading legal holds…'}</footer>
        </section>

        <section>
          <h3>Malware scanning</h3>
          <label className="is-check"><input type="checkbox" checked={form.malwareRequired} onChange={event => change('malwareRequired', event.target.checked)} /> Require a clean verdict</label>
          <label className="is-check"><input type="checkbox" checked={form.blockScanErrors} onChange={event => change('blockScanErrors', event.target.checked)} /> Block files when scanning fails</label>
          <label>Provider <input aria-label="Malware scanning provider" value={form.malwareProvider} onChange={event => change('malwareProvider', event.target.value)} /></label>
        </section>
      </div>

      <section className="document-governance__failures">
        <div><h3>Operations needing attention</h3>{failures && <span aria-label={`${failures.length} failed operations`}>{failures.length}</span>}</div>
        {failures === null ? (!loadErrors['failed operations'] && <p role="status">Loading failed operations…</p>) : failures.length ? (
          <ul>{failures.map(item => (
            <li key={`${item.category}-${item.operation_id}`}>
              <strong>{item.category.replace('_', ' ')}</strong>
              <code>{item.operation_id}</code>
              <span>{item.error || item.status}</span>
            </li>
          ))}</ul>
        ) : <p>No failed document operations.</p>}
      </section>
    </div>
  )
}
